import { useState, useEffect } from 'react'
import { cn } from "@/utils/cn"
import { 
  Info, 
  AlertTriangle, 
  XCircle, 
  CheckCircle, 
  AlertCircle,
  X 
} from "lucide-react"
import { Button } from "@/components/ui/button"

interface AlertBannerProps {
  type: 'info' | 'warning' | 'error' | 'success' | 'critical'
  title: string
  message?: string
  dismissible?: boolean
  action?: {
    label: string
    onClick: () => void
  }
  autoHide?: number
  onDismiss?: () => void
  className?: string
}

export const AlertBanner = ({
  type,
  title,
  message,
  dismissible = false,
  action,
  autoHide,
  onDismiss,
  className
}: AlertBannerProps) => {
  const [isVisible, setIsVisible] = useState(true)

  useEffect(() => {
    if (autoHide && autoHide > 0) {
      const timer = setTimeout(() => {
        handleDismiss()
      }, autoHide)

      return () => clearTimeout(timer)
    }
  }, [autoHide])

  const handleDismiss = () => {
    setIsVisible(false)
    onDismiss?.()
  }

  const config = {
    info: {
      icon: Info,
      className: 'bg-blue-50 border-blue-200 text-blue-800 dark:bg-blue-900/20 dark:border-blue-800 dark:text-blue-400'
    },
    warning: {
      icon: AlertTriangle,
      className: 'bg-yellow-50 border-yellow-200 text-yellow-800 dark:bg-yellow-900/20 dark:border-yellow-800 dark:text-yellow-400'
    },
    error: {
      icon: XCircle,
      className: 'bg-red-50 border-red-200 text-red-800 dark:bg-red-900/20 dark:border-red-800 dark:text-red-400'
    },
    success: {
      icon: CheckCircle,
      className: 'bg-green-50 border-green-200 text-green-800 dark:bg-green-900/20 dark:border-green-800 dark:text-green-400'
    },
    critical: {
      icon: AlertCircle,
      className: 'bg-red-100 border-red-300 text-red-900 dark:bg-red-900/30 dark:border-red-700 dark:text-red-300 animate-pulse'
    }
  }

  const { icon: IconComponent, className: typeClassName } = config[type]

  if (!isVisible) return null

  return (
    <div
      className={cn(
        "flex items-start gap-3 p-4 border rounded-lg transition-all duration-300 animate-slide-in",
        typeClassName,
        className
      )}
      role="alert"
      aria-live={type === 'critical' ? 'assertive' : 'polite'}
    >
      <IconComponent 
        className="h-5 w-5 mt-0.5 flex-shrink-0" 
        aria-hidden="true"
      />
      
      <div className="flex-1 min-w-0">
        <h4 className="font-semibold text-sm">
          {title}
        </h4>
        {message && (
          <p className="mt-1 text-sm opacity-90">
            {message}
          </p>
        )}
      </div>

      <div className="flex items-center gap-2 flex-shrink-0">
        {action && (
          <Button
            variant="outline"
            size="sm"
            onClick={action.onClick}
            className="text-xs h-7"
          >
            {action.label}
          </Button>
        )}
        
        {dismissible && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleDismiss}
            className="h-7 w-7 p-0 hover:bg-black/10 dark:hover:bg-white/10"
            aria-label="Dismiss alert"
          >
            <X className="h-3 w-3" />
          </Button>
        )}
      </div>
    </div>
  )
}